// ============================================================
// PHS Status Chip Component
// Enterprise status visualization with consistent styling
// ============================================================

import React from 'react';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';

// Status configuration mapping
const STATUS_CONFIG = {
  // INTAKE
  "new": { label: "New", tone: "blue", group: "intake" },
  "triage_required": { label: "Triage Required", tone: "amber", group: "intake" },
  "awaiting_assignment": { label: "Awaiting Assignment", tone: "amber", group: "intake" },
  
  // CONTRACTOR
  "assigned": { label: "Assigned", tone: "blue", group: "contractor" },
  "awaiting_acceptance": { label: "Awaiting Acceptance", tone: "amber", group: "contractor" },
  "accepted": { label: "Accepted", tone: "green", group: "contractor" },
  "awaiting_quote": { label: "Awaiting Quote", tone: "amber", group: "contractor" },
  
  // QUOTE
  "quote_submitted": { label: "Quote Submitted", tone: "blue", group: "quote" },
  "quote_under_review": { label: "Quote Under Review", tone: "purple", group: "quote" },
  "quote_revision_requested": { label: "Revision Requested", tone: "amber", group: "quote" },
  "quote_sent_for_approval": { label: "Sent For Approval", tone: "blue", group: "quote" },
  "quote_approved": { label: "Quote Approved", tone: "green", group: "quote" },
  "quote_rejected": { label: "Quote Rejected", tone: "red", group: "quote" },
  
  // DELIVERY
  "po_issued": { label: "PO Issued", tone: "green", group: "delivery" },
  "scheduled": { label: "Scheduled", tone: "blue", group: "delivery" },
  "in_progress": { label: "In Progress", tone: "purple", group: "delivery" },
  "follow_on_required": { label: "Follow-On Required", tone: "amber", group: "delivery" },
  "variation_under_review": { label: "Variation Review", tone: "amber", group: "delivery" },
  
  // COMPLETION
  "completed_awaiting_evidence": { label: "Awaiting Evidence", tone: "amber", group: "completion" },
  "satisfaction_pending": { label: "Satisfaction Pending", tone: "amber", group: "completion" },
  "ready_for_invoice": { label: "Ready For Invoice", tone: "green", group: "completion" },
  
  // FINANCE
  "invoice_submitted": { label: "Invoice Submitted", tone: "blue", group: "finance" },
  "invoice_under_review": { label: "Invoice Review", tone: "purple", group: "finance" },
  "approved_for_payment": { label: "Approved For Payment", tone: "green", group: "finance" },
  "paid": { label: "Paid", tone: "green", group: "finance" },
  
  // CLOSURE
  "closed": { label: "Closed", tone: "slate", group: "closure" },
  "cancelled": { label: "Cancelled", tone: "red", group: "closure" },
  
  // LEGACY MAPPINGS
  "New": { label: "New", tone: "blue", group: "intake" },
  "Job Created": { label: "New", tone: "blue", group: "intake" },
  "Awaiting Quote": { label: "Awaiting Quote", tone: "amber", group: "contractor" },
  "Quote Received": { label: "Quote Submitted", tone: "blue", group: "quote" },
  "Quote Sent": { label: "Sent For Approval", tone: "blue", group: "quote" },
  "Approved": { label: "Quote Approved", tone: "green", group: "quote" },
  "PO Issued": { label: "PO Issued", tone: "green", group: "delivery" },
  "Assigned": { label: "Assigned", tone: "blue", group: "contractor" },
  "Scheduled": { label: "Scheduled", tone: "blue", group: "delivery" },
  "In Progress": { label: "In Progress", tone: "purple", group: "delivery" },
  "Completed": { label: "Completed", tone: "green", group: "completion" },
  "Approved for Invoicing": { label: "Ready For Invoice", tone: "green", group: "completion" },
  "Closed": { label: "Closed", tone: "slate", group: "closure" },
  "Cancelled": { label: "Cancelled", tone: "red", group: "closure" },
  "Work Scheduled": { label: "Scheduled", tone: "blue", group: "delivery" },
  "Sent to Contractor": { label: "Assigned", tone: "blue", group: "contractor" },
  "Quote Requested": { label: "Awaiting Quote", tone: "amber", group: "contractor" },
  "Manual Intervention Required": { label: "Triage Required", tone: "amber", group: "intake" }
};

// Tone to Tailwind class mapping
const TONE_CLASSES = {
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  green: "bg-green-100 text-green-800 border-green-200",
  red: "bg-red-100 text-red-800 border-red-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
  teal: "bg-teal-100 text-teal-800 border-teal-200"
};

// Normalize status to canonical form
const normalizeStatus = (status) => {
  if (!status) return "new";
  const normalized = status.toString().toLowerCase().replace(/\s+/g, '_');
  return normalized;
};

// Get config for a status
const getStatusConfig = (status) => {
  // Try exact match first
  if (STATUS_CONFIG[status]) return STATUS_CONFIG[status];
  
  // Try normalized
  const normalized = normalizeStatus(status);
  if (STATUS_CONFIG[normalized]) return STATUS_CONFIG[normalized];
  
  // Default
  return { label: status, tone: "slate", group: "unknown" };
};

// Main StatusChip component
export const StatusChip = ({ 
  status, 
  size = "default", 
  showBorder = false,
  className = "" 
}) => {
  const config = getStatusConfig(status);
  const toneClass = TONE_CLASSES[config.tone] || TONE_CLASSES.slate;
  
  const sizeClasses = {
    small: "px-2 py-0.5 text-[10px]",
    default: "px-3 py-1 text-xs",
    large: "px-4 py-1.5 text-sm"
  };
  
  return (
    <span 
      className={`
        inline-flex items-center rounded-full font-semibold
        ${sizeClasses[size] || sizeClasses.default}
        ${toneClass}
        ${showBorder ? 'border' : ''}
        ${className}
      `}
    >
      {config.label}
    </span>
  );
};

// Priority chip
export const PriorityChip = ({ priority, size = "default", className = "" }) => {
  const priorityConfig = {
    emergency: { label: "Emergency", tone: "red" },
    urgent: { label: "Urgent", tone: "amber" },
    high: { label: "High", tone: "amber" },
    normal: { label: "Normal", tone: "blue" },
    medium: { label: "Medium", tone: "blue" },
    low: { label: "Low", tone: "slate" }
  };
  
  const normalized = (priority || "normal").toLowerCase();
  const config = priorityConfig[normalized] || priorityConfig.normal;
  const toneClass = TONE_CLASSES[config.tone] || TONE_CLASSES.blue;
  
  const sizeClasses = {
    small: "px-2 py-0.5 text-[10px]",
    default: "px-3 py-1 text-xs",
    large: "px-4 py-1.5 text-sm"
  };
  
  return (
    <span 
      className={`
        inline-flex items-center rounded-full font-semibold
        ${sizeClasses[size] || sizeClasses.default}
        ${toneClass}
        ${className}
      `}
    >
      {config.label}
    </span>
  );
};

// SLA Badge - simple green/amber/red indicator
export const SLABadge = ({ 
  hoursOld, 
  targetHours = 48, 
  size = "default",
  showLabel = true,
  className = "" 
}) => {
  // Calculate SLA status
  const percentUsed = (hoursOld / targetHours) * 100;
  
  let status, label, Icon;
  if (percentUsed >= 100) {
    status = 'overdue';
    label = 'Overdue';
    Icon = AlertTriangle;
  } else if (percentUsed >= 75) {
    status = 'approaching';
    label = 'At Risk';
    Icon = Clock;
  } else {
    status = 'on_track';
    label = 'On Track';
    Icon = CheckCircle;
  }
  
  const statusClasses = {
    overdue: 'bg-red-100 text-red-700 border-red-200',
    approaching: 'bg-amber-100 text-amber-700 border-amber-200',
    on_track: 'bg-green-100 text-green-700 border-green-200'
  };
  
  const dotClasses = {
    overdue: 'bg-red-500',
    approaching: 'bg-amber-500',
    on_track: 'bg-green-500'
  };
  
  const sizeClasses = {
    small: "px-1.5 py-0.5 text-[10px] gap-1",
    default: "px-2 py-1 text-xs gap-1.5",
    large: "px-3 py-1.5 text-sm gap-2"
  };
  
  const dotSizes = {
    small: "w-1.5 h-1.5",
    default: "w-2 h-2",
    large: "w-2.5 h-2.5"
  };
  
  return (
    <span 
      className={`
        inline-flex items-center rounded-full font-medium border
        ${sizeClasses[size] || sizeClasses.default}
        ${statusClasses[status]}
        ${className}
      `}
      title={`${Math.round(hoursOld)}h / ${targetHours}h target`}
    >
      <span className={`rounded-full ${dotSizes[size]} ${dotClasses[status]}`} />
      {showLabel && <span>{label}</span>}
    </span>
  );
};

// Compact SLA dot (for tight spaces)
export const SLADot = ({ hoursOld, targetHours = 48, className = "" }) => {
  const percentUsed = (hoursOld / targetHours) * 100;
  
  let colorClass;
  if (percentUsed >= 100) colorClass = 'bg-red-500';
  else if (percentUsed >= 75) colorClass = 'bg-amber-500';
  else colorClass = 'bg-green-500';
  
  return (
    <span 
      className={`inline-block w-2 h-2 rounded-full ${colorClass} ${className}`}
      title={`SLA: ${Math.round(hoursOld)}h / ${targetHours}h`}
    />
  );
};

// Status group badge
export const StatusGroupBadge = ({ group, className = "" }) => {
  const groupLabels = {
    intake: "Intake",
    contractor: "Contractor",
    quote: "Quote",
    delivery: "Delivery",
    completion: "Completion",
    finance: "Finance",
    closure: "Closure"
  };
  
  return (
    <span className={`text-[10px] uppercase tracking-wide text-slate-500 font-medium ${className}`}>
      {groupLabels[group] || group}
    </span>
  );
};

// Status with group
export const StatusWithGroup = ({ status, size = "default", className = "" }) => {
  const config = getStatusConfig(status);
  
  return (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <StatusGroupBadge group={config.group} />
      <StatusChip status={status} size={size} />
    </div>
  );
};

// Status chip with SLA indicator combined
export const StatusChipWithSLA = ({ 
  status, 
  hoursOld, 
  targetHours = 48,
  size = "default",
  className = "" 
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <StatusChip status={status} size={size} />
      {hoursOld !== undefined && (
        <SLADot hoursOld={hoursOld} targetHours={targetHours} />
      )}
    </div>
  );
};

// Export utilities
export const getStatusLabel = (status) => getStatusConfig(status).label;
export const getStatusTone = (status) => getStatusConfig(status).tone;
export const getStatusGroup = (status) => getStatusConfig(status).group;
export const isTerminalStatus = (status) => {
  const normalized = normalizeStatus(status);
  return normalized === "closed" || normalized === "cancelled";
};

export default StatusChip;
